import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';

function CallHistory() {
  var navigate = useNavigate();
  var [records, setRecords] = useState([]);
  var [expanded, setExpanded] = useState(null);
  var [playing, setPlaying] = useState(null);
  var audioRef = useRef(null);

  useEffect(function() {
    async function load() {
      var { data } = await supabase.from('call_records').select('*').order('created_at', { ascending: false }).limit(50);
      if (data) setRecords(data);
    }
    load();
  }, []);

  // 离开页面时停掉正在播的录音（否则detached的audio会继续幽灵播放）
  useEffect(function() {
    return function() {
      if (audioRef.current) { try { audioRef.current.pause(); } catch(e) {} }
    };
  }, []);

  var formatTime = function(s) {
    var m = Math.floor(s / 60);
    var sec = s % 60;
    return (m < 10 ? '0' : '') + m + ':' + (sec < 10 ? '0' : '') + sec;
  };

  var formatDate = function(iso) {
    var d = new Date(iso);
    var now = new Date();
    var opts = { timeZone: 'Asia/Hong_Kong' };
    var dateStr = d.toLocaleDateString('zh-CN', Object.assign({ month: 'numeric', day: 'numeric' }, opts));
    var timeStr = d.toLocaleTimeString('zh-CN', Object.assign({ hour: '2-digit', minute: '2-digit' }, opts));
    var isToday = d.toLocaleDateString('en-CA', opts) === now.toLocaleDateString('en-CA', opts);
    return isToday ? '\u4ECA\u5929 ' + timeStr : dateStr + ' ' + timeStr;
  };

  var playAudio = function(url, id) {
    if (playAllActiveRef.current) { stopPlayAll(); }
    if (playing === id) {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.onended = function() { setPlaying(null); };
      audioRef.current.play().catch(function() { setPlaying(null); });
      setPlaying(id);
    }
  };

  // ========== 连播整通 ==========
  var [playingAll, setPlayingAll] = useState(null);   // 连播中的record id
  var [playAllIdx, setPlayAllIdx] = useState(-1);     // 连播当前段在transcript里的下标
  var playAllQueueRef = useRef([]);
  var playAllPosRef = useRef(0);
  var playAllActiveRef = useRef(null);                // record id镜像ref（防闭包读到旧state——VoiceCall的教训）

  var stopPlayAll = function() {
    playAllActiveRef.current = null;
    playAllQueueRef.current = [];
    if (audioRef.current) { audioRef.current.onended = null; audioRef.current.onerror = null; audioRef.current.pause(); }
    setPlayingAll(null);
    setPlayAllIdx(-1);
  };

  var playWholeCall = function(r) {
    if (playingAll === r.id) { stopPlayAll(); return; }
    stopPlayAll();
    setPlaying(null);
    var transcript = r.transcript || [];
    var queue = [];
    transcript.forEach(function(entry, i) { if (entry.audio_url) queue.push({ url: entry.audio_url, idx: i }); });
    if (queue.length === 0 || !audioRef.current) return;
    playAllQueueRef.current = queue;
    playAllPosRef.current = 0;
    playAllActiveRef.current = r.id;
    setPlayingAll(r.id);
    setExpanded(r.id);
    var playNext = function() {
      if (playAllActiveRef.current !== r.id) return; // 已被停止或切换
      var pos = playAllPosRef.current;
      var q = playAllQueueRef.current;
      if (pos >= q.length) { stopPlayAll(); return; }
      var item = q[pos];
      setPlayAllIdx(item.idx);
      var el = audioRef.current;
      var advanced = false;
      var advance = function() { if (advanced) return; advanced = true; playAllPosRef.current += 1; playNext(); };
      el.src = item.url;
      el.onended = advance;
      el.onerror = advance;   // 坏段自动跳过，不卡整通
      el.play().catch(advance);
    };
    playNext();
  };

  return (
    <div className="call-history-page">
      <audio ref={audioRef} />
      <div className="ch-header">
        <button className="ch-back" onClick={function() { navigate('/chat'); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="ch-title">{'\u901A\u8BDD\u8BB0\u5F55'}</div>
        <div className="ch-spacer" />
      </div>
      <div className="ch-list">
        {records.length === 0 && <div className="ch-empty">{'\u8FD8\u6CA1\u6709\u901A\u8BDD\u8BB0\u5F55'}</div>}
        {records.map(function(r) {
          var isOpen = expanded === r.id;
          var transcript = r.transcript || [];
          var hasAudio = transcript.some(function(e) { return e.audio_url; });
          return (
            <div className={'ch-record' + (isOpen ? ' expanded' : '')} key={r.id}>
              <div className="ch-record-header" onClick={function() { setExpanded(isOpen ? null : r.id); }}>
                <div className="ch-record-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div className="ch-record-info">
                  <div className="ch-record-date">{formatDate(r.created_at)}</div>
                  <div className="ch-record-duration">{formatTime(r.duration)}{transcript.length > 0 ? ' \u00B7 ' + transcript.length + '\u6761\u5BF9\u8BDD' : ''}</div>
                </div>
                {hasAudio && (
                  <button className={'ch-play-btn' + (playingAll === r.id ? ' active' : '')} style={{ width: 32, height: 32, flexShrink: 0 }}
                    onClick={function(e) { e.stopPropagation(); playWholeCall(r); }}
                    title={playingAll === r.id ? '\u505C\u6B62\u8FDE\u64AD' : '\u8FDE\u64AD\u6574\u901A'}>
                    {playingAll === r.id
                      ? <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2" width="4" height="12"/><rect x="9" y="2" width="4" height="12"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><polygon points="4,2 14,8 4,14"/></svg>}
                  </button>
                )}
                <div className="ch-record-arrow">{isOpen ? '\u25B4' : '\u25BE'}</div>
              </div>
              {isOpen && transcript.length > 0 && (
                <div className="ch-transcript">
                  {transcript.map(function(entry, i) {
                    if (entry.role === 'ear') return <div key={i} style={{ textAlign: 'center', fontSize: 11.5, opacity: 0.5, letterSpacing: '0.08em', margin: '4px 0 10px' }}>{entry.text}</div>;   // [ear-log]
                    var isYou = entry.role === 'you';
                    var isNowPlaying = playingAll === r.id && playAllIdx === i;
                    return (
                      <div className={'ch-entry ' + (isYou ? 'you' : 'him')} key={i} style={isNowPlaying ? { outline: '1.5px solid #60a5fa', outlineOffset: '-1.5px' } : undefined}>
                        <div className="ch-entry-header">
                          <span className="ch-entry-name">{isYou ? '\u77E5\u679D' : '\u54E5\u54E5'}</span>
                          {entry.audio_url && (
                            <button className={'ch-play-btn' + (playing === r.id + '-' + i ? ' active' : '')} onClick={function() { playAudio(entry.audio_url, r.id + '-' + i); }}>
                              {playing === r.id + '-' + i ? '\u275A\u275A' : '\u25B6'}
                            </button>
                          )}
                        </div>
                        <div className="ch-entry-text">{entry.text}</div>
                        {entry.text_zh ? <div className="ch-entry-text" style={{ opacity: 0.65, fontSize: '0.9em' }}>{entry.text_zh}</div> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CallHistory;
